import { useEffect } from 'react';

const BACKEND =
  process.env.REACT_APP_BACKEND_URL ||
  (typeof window !== 'undefined' && window.location.port === '3000'
    ? 'http://localhost:5000'
    : '');
const BASE = `${BACKEND}/api/admin`;
const CAREERS_BASE = `${BACKEND}/api/careers`;
const json = { 'Content-Type': 'application/json' };

const message = async res => {
  const body = await res.json().catch(() => ({}));
  const detail = body.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map(d => d?.msg || '').filter(Boolean).join(' ');
  return 'Something went wrong. Please try again.';
};

const getAuthHeaders = (extra = {}) => {
  const token = typeof localStorage !== 'undefined' ? localStorage.getItem('techspitze_admin_token') : null;
  return {
    ...json,
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...extra,
  };
};

export const adminApi = {
  async login(password) {
    const res = await fetch(`${BASE}/login`, { method: 'POST', headers: json, credentials: 'include', body: JSON.stringify({ password }) });
    if (res.ok) {
      const data = await res.json().catch(() => ({}));
      if (data?.token) {
        localStorage.setItem('techspitze_admin_token', data.token);
      }
      return { ok: true };
    }
    return { ok: false, error: await message(res) };
  },
  async logout() {
    if (typeof localStorage !== 'undefined') localStorage.removeItem('techspitze_admin_token');
    await fetch(`${BASE}/logout`, { method: 'POST', credentials: 'include' });
  },
  async me() {
    const res = await fetch(`${BASE}/me`, { headers: getAuthHeaders(), credentials: 'include' });
    return res.ok;
  },
  async list(params) {
    const res = await fetch(`${BASE}/discovery/submissions?${params}`, { headers: getAuthHeaders(), credentials: 'include' });
    if (res.status === 401) return { unauthorized: true };
    if (!res.ok) throw new Error(await message(res));
    return res.json();
  },
  async update(id, patch) {
    const res = await fetch(`${BASE}/discovery/submissions/${id}`, { method: 'PATCH', headers: getAuthHeaders(), credentials: 'include', body: JSON.stringify(patch) });
    if (!res.ok) throw new Error(await message(res));
    return res.json();
  },
  async remove(id) {
    const res = await fetch(`${BASE}/discovery/submissions/${id}`, { method: 'DELETE', headers: getAuthHeaders(), credentials: 'include' });
    if (!res.ok) throw new Error(await message(res));
    return res.json();
  },

  // Careers & Candidate Applications
  async listCandidates(params = '') {
    const res = await fetch(`${CAREERS_BASE}/applications?${params}`, { headers: getAuthHeaders(), credentials: 'include' });
    if (res.status === 401) return { unauthorized: true };
    if (!res.ok) throw new Error(await message(res));
    return res.json();
  },
  async getCandidate(id) {
    const res = await fetch(`${CAREERS_BASE}/applications/${id}`, { headers: getAuthHeaders(), credentials: 'include' });
    if (!res.ok) throw new Error(await message(res));
    return res.json();
  },
  async updateCandidate(id, patch) {
    const res = await fetch(`${CAREERS_BASE}/applications/${id}`, {
      method: 'PATCH',
      headers: getAuthHeaders(),
      credentials: 'include',
      body: JSON.stringify(patch),
    });
    if (!res.ok) throw new Error(await message(res));
    return res.json();
  },
  async deleteCandidate(id) {
    const res = await fetch(`${CAREERS_BASE}/applications/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
      credentials: 'include',
    });
    if (!res.ok) throw new Error(await message(res));
    return res.json();
  },
  async getCareerStats() {
    const res = await fetch(`${CAREERS_BASE}/stats`, { headers: getAuthHeaders(), credentials: 'include' });
    if (!res.ok) throw new Error(await message(res));
    return res.json();
  },
  getResumeUrl(id, download = false) {
    return `${CAREERS_BASE}/applications/${id}/resume${download ? '?download=true' : ''}`;
  },
  getCoverNoteUrl(id, download = false) {
    return `${CAREERS_BASE}/applications/${id}/cover-note${download ? '?download=true' : ''}`;
  },
  exportCandidatesCsv(applications) {
    const headers = ['Full Name', 'Role Applied', 'Job ID', 'Email', 'Phone', 'Location', 'Experience', 'Portfolio URL', 'Resume Filename', 'Status', 'Applied Date'];
    const rows = applications.map(app => [
      `"${(app.full_name || '').replace(/"/g, '""')}"`,
      `"${(app.job_title || '').replace(/"/g, '""')}"`,
      `"${(app.job_id || '').replace(/"/g, '""')}"`,
      `"${(app.email || '').replace(/"/g, '""')}"`,
      `"${(app.phone || '').replace(/"/g, '""')}"`,
      `"${(app.location || '').replace(/"/g, '""')}"`,
      `"${(app.experience_years || '').replace(/"/g, '""')}"`,
      `"${(app.portfolio_url || '').replace(/"/g, '""')}"`,
      `"${(app.resume?.filename || '').replace(/"/g, '""')}"`,
      `"${(app.status || 'submitted').replace(/"/g, '""')}"`,
      `"${(app.created_at || '').replace(/"/g, '""')}"`,
    ]);
    const csvContent = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `candidate-applications-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  },

  // Opportunities Management
  async listOpportunities(params = '') {
    const res = await fetch(`${CAREERS_BASE}/opportunities?${params}`, { headers: getAuthHeaders(), credentials: 'include' });
    if (!res.ok) throw new Error(await message(res));
    return res.json();
  },
  async getOpportunity(id) {
    const res = await fetch(`${CAREERS_BASE}/opportunities/${id}`, { headers: getAuthHeaders(), credentials: 'include' });
    if (!res.ok) throw new Error(await message(res));
    return res.json();
  },
  async createOpportunity(payload) {
    const res = await fetch(`${CAREERS_BASE}/opportunities`, {
      method: 'POST',
      headers: getAuthHeaders(),
      credentials: 'include',
      body: JSON.stringify(payload),
    });
    if (!res.ok) throw new Error(await message(res));
    return res.json();
  },
  async updateOpportunity(id, payload) {
    const res = await fetch(`${CAREERS_BASE}/opportunities/${id}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      credentials: 'include',
      body: JSON.stringify(payload),
    });
    if (!res.ok) throw new Error(await message(res));
    return res.json();
  },
  async deleteOpportunity(id) {
    const res = await fetch(`${CAREERS_BASE}/opportunities/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
      credentials: 'include',
    });
    if (!res.ok) throw new Error(await message(res));
    return res.json();
  },
  async exportCsv(params, scope) {    const res = await fetch(`${BASE}/discovery/export?${params}&scope=${scope}`, { credentials: 'include' });
    if (!res.ok) throw new Error(await message(res));
    const blob = await res.blob();
    const stamp = new Date().toISOString().slice(0, 10);
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `techspitze-discovery-${stamp}.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  },
  absolute(url) {
    return url && url.startsWith('/api/') ? `${process.env.REACT_APP_BACKEND_URL}${url}` : url;
  },
  async upload(file) {
    const body = new FormData();
    body.append('file', file);
    const res = await fetch(`${BASE}/uploads`, { method: 'POST', credentials: 'include', body });
    if (!res.ok) { toastError(await message(res)); return {}; }
    return res.json();
  },
  async listContent(kind, params = '') {
    const res = await fetch(`${BASE}/${kind}?${params}`, { credentials: 'include' });
    if (res.status === 401) return { unauthorized: true };
    if (!res.ok) throw new Error(await message(res));
    return res.json();
  },
  async getContent(kind, id) {
    const res = await fetch(`${BASE}/${kind}/${id}`, { credentials: 'include' });
    if (!res.ok) throw new Error(await message(res));
    return res.json();
  },
  async saveContent(kind, id, payload) {
    const res = await fetch(`${BASE}/${kind}${id ? `/${id}` : ''}`, {
      method: id ? 'PUT' : 'POST', headers: json, credentials: 'include', body: JSON.stringify(payload),
    });
    if (!res.ok) throw new Error(await message(res));
    return res.json();
  },
  async deleteContent(kind, id) {
    const res = await fetch(`${BASE}/${kind}/${id}`, { method: 'DELETE', credentials: 'include' });
    if (!res.ok) throw new Error(await message(res));
    return res.json();
  },
};

const toastError = text => { import('sonner').then(({ toast }) => toast.error(text)); };

export const slugify = value => String(value || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 90);

export const useNoIndex = () => useEffect(() => {  const tag = document.createElement('meta');
  tag.name = 'robots';
  tag.content = 'noindex,nofollow';
  document.head.appendChild(tag);
  return () => tag.remove();
}, []);

export const STATUSES = ['New', 'Contacted', 'Qualified', 'Proposal', 'Won', 'Lost'];
export const statusClass = status => `status-${String(status || 'New').toLowerCase()}`;
export const CANDIDATE_STATUSES = ['submitted', 'reviewed', 'shortlisted', 'interviewed', 'rejected'];
export const candidateStatusClass = status => `status-${String(status || 'submitted').toLowerCase()}`;
export const formatDate = iso => (iso ? new Date(iso).toLocaleDateString('en-CA', { year: 'numeric', month: 'short', day: 'numeric' }) : '—');
export const formatDateTime = iso => (iso ? new Date(iso).toLocaleString('en-CA', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—');
export const answerText = value => (Array.isArray(value) ? value.join(', ') : value || '');
